'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, DoneAll, Warning } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, Card, Grid, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { PDFDownloadLink } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import CertificatePDF from '@/components/course/certificate-pdf';
import BuyCoursePrompt from '@/components/shared/buy-course-prompt';
import { useUpdateUser } from '@/hooks';
import { useAuth, useCourses } from '@/providers';
import { type CurrentUserForm, currentUserSchema } from '@/utils/server/validations';

export default function CourseCertificate() {
  const { courseStatus } = useCourses();
  const { currentUser } = useAuth();
  const { mutate: updateUser } = useUpdateUser();
  const [showPdfButton, setShowPdfButton] = useState(false);
  const { selectedCourse } = useCourses();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CurrentUserForm>({
    resolver: zodResolver(currentUserSchema),
    defaultValues: {
      firstName: currentUser?.firstName || '',
      lastName: currentUser?.lastName || '',
      birthDate: currentUser?.birthDate || '',
      address: {
        street: currentUser?.address?.street || '',
        zipCode: currentUser?.address?.zipCode || '',
        city: currentUser?.address?.city || '',
        country: currentUser?.address?.country || 'Deutschland',
      },
    },
  });

  if (!courseStatus || !currentUser || !selectedCourse) {
    return null;
  }

  if (!courseStatus.purchasedAt) {
    return (
      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          Deine Teilnahmebescheinigung
        </Typography>
        <BuyCoursePrompt variant="outlined" wpProductId={selectedCourse?.wpProductId}>
          Kaufe den Kurs, um Deine Bescheinigung ausstellen zu können.
        </BuyCoursePrompt>
      </Card>
    );
  }

  const hasRequiredUserData = !!(
    currentUser.birthDate &&
    currentUser.address?.street &&
    currentUser.address?.zipCode &&
    currentUser.address?.city
  );

  const onSubmit: SubmitHandler<CurrentUserForm> = (data) => {
    updateUser(data, {
      onSuccess: () => {
        setShowPdfButton(true);
      },
    });
  };

  if (courseStatus.exercises.total !== courseStatus.exercises.correct) {
    return (
      <Card variant="outlined" sx={{ p: 2, mb: 2, userSelect: 'none' }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          Deine Teilnahmebescheinigung
        </Typography>
        <Alert severity="info" variant="outlined">
          <AlertTitle>Anforderungen</AlertTitle>
          Beantworte alle Übungen <strong>richtig</strong> (es erscheint ein{' '}
          <Check fontSize="small" sx={{ verticalAlign: 'text-bottom' }} /> für ausgewertet und{' '}
          <DoneAll color="success" fontSize="small" sx={{ verticalAlign: 'text-bottom' }} /> für
          alle Fragen richtig beantwortet), um hier Deine Bescheinigung ausstellen zu können.
        </Alert>
      </Card>
    );
  }

  if (!hasRequiredUserData && !showPdfButton) {
    return (
      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          Deine Teilnahmebescheinigung
        </Typography>
        <Alert icon={<Warning />} severity="warning" variant="outlined" sx={{ mb: 3 }}>
          <AlertTitle>Wichtiger Hinweis</AlertTitle>
          Die folgenden Daten werden für Dein Teilnahmebescheinigung benötigt und können nach der
          Eingabe nicht mehr geändert werden. Bitte prüfe Deine Eingaben sorgfältig.
        </Alert>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                {...register('firstName')}
                label="Vorname"
                fullWidth
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                {...register('lastName')}
                label="Nachname"
                fullWidth
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DatePicker
                label="Geburtsdatum"
                value={watch('birthDate') ? dayjs(watch('birthDate')) : null}
                onChange={(date) => setValue('birthDate', date?.format('YYYY-MM-DD') || '')}
                format="DD.MM.YYYY"
                minDate={dayjs('1900-01-01')}
                maxDate={dayjs()}
                slotProps={{
                  textField: {
                    error: !!errors.birthDate,
                    helperText: errors.birthDate?.message,
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                {...register('address.street')}
                label="Straße und Hausnummer"
                fullWidth
                error={!!errors.address?.street}
                helperText={errors.address?.street?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                {...register('address.zipCode')}
                label="PLZ"
                fullWidth
                error={!!errors.address?.zipCode}
                helperText={errors.address?.zipCode?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                {...register('address.city')}
                label="Stadt"
                fullWidth
                error={!!errors.address?.city}
                helperText={errors.address?.city?.message}
              />
            </Grid>
          </Grid>

          <Button type="submit" variant="contained" color="primary">
            Daten speichern
          </Button>
        </Box>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        Deine Teilnahmebescheinigung
      </Typography>
      <Alert
        severity="success"
        sx={{ mb: 2 }}
        variant="outlined"
        action={
          <PDFDownloadLink
            document={
              <CertificatePDF
                user={currentUser}
                courseTitle={selectedCourse.title}
                purchasedAt={courseStatus.purchasedAt}
              />
            }
            fileName={`${selectedCourse.title.replace(/\s+/g, '-')} Teilnahmebescheinigung.pdf`}>
            {({ loading, url }) => (
              <Button
                variant="contained"
                disabled={loading}
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  if (url) {
                    window.open(url, '_blank');
                  }
                }}>
                PDF öffnen
              </Button>
            )}
          </PDFDownloadLink>
        }>
        <AlertTitle>Bescheinigung verfügbar</AlertTitle>
        Du kannst jetzt deine Bescheinigung als PDF öffnen.
      </Alert>
    </Card>
  );
}
